import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

// --- helpers ---
function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function stripOuterParens(s) {
  let t = (s || "").trim();
  if (t.startsWith("(") && t.endsWith(")")) {
    // zdejmij tylko jedną parę
    t = t.slice(1, -1).trim();
  }
  return t;
}

// Normalizacja „sumy składników” -> akceptuje przemienność dodawania (np. x+7 == 7+x)
// NIE robi „przemienności odejmowania”, bo minus zamieniamy na "+-" i sortujemy składniki.
function normalizeSum(expr) {
  return String(expr ?? "")
    .replace(/−/g, "-")
    .replace(/\s+/g, "")
    .replace(/\*/g, "")
    .toLowerCase()
    .replace(/\\dfrac\{([^}]+)\}\{([^}]+)\}/g, "$1/$2")
    .replace(/-/g, "+-")
    .split("+")
    .filter(Boolean)
    .sort()
    .join("+");
}

function splitFraction(raw) {
  const s = String(raw ?? "").trim();

  // akceptuj zapis \dfrac{...}{...} wpisany ręcznie
  const dfracMatch = s.match(/\\dfrac\{(.+)\}\{(.+)\}/);
  if (dfracMatch) return [dfracMatch[1], dfracMatch[2]];

  // normalny zapis num/den – bierzemy pierwszy slash
  const idx = s.indexOf("/");
  if (idx === -1) return [null, null];

  const num = s.slice(0, idx);
  const den = s.slice(idx + 1);
  return [num, den];
}

/**
 * Render-latex dla "Twoja odpowiedź":
 * - 3 1/2 -> 3\dfrac{1}{2}
 * - -3/4 -> \dfrac{-3}{4}
 * - * i · -> \cdot
 * - oraz: jeśli uczeń wpisze licznik/mianownik jako "a/b" na top-level,
 *   to pokaż \dfrac{a}{b} (bez konieczności nawiasów).
 */
function findTopLevelSlash(s) {
  let par = 0,
    br = 0,
    sq = 0;

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "(") par++;
    else if (ch === ")") par = Math.max(0, par - 1);
    else if (ch === "{") br++;
    else if (ch === "}") br = Math.max(0, br - 1);
    else if (ch === "[") sq++;
    else if (ch === "]") sq = Math.max(0, sq - 1);
    else if (ch === "/" && par === 0 && br === 0 && sq === 0) return i;
  }
  return -1;
}

function toUserAnswerLatex(raw) {
  let s = String(raw ?? "");

  // mieszane ułamki: "3 1/2" -> "3\\dfrac{1}{2}"
  s = s.replace(
    /(-?\d+)\s+(\d+)\/(\d+)/g,
    (_, w, n, d) => `${w}\\dfrac{${n}}{${d}}`
  );

  // zwykłe ułamki liczbowe: "-3/4" -> "\\dfrac{-3}{4}"
  s = s.replace(
    /(?<!\\dfrac\{)(-?\d+)\/(\d+)/g,
    (_, n, d) => `\\dfrac{${n}}{${d}}`
  );

  // wielocyfrowe wykładniki
  s = s.replace(/\^(-?\d{2,})/g, "^{$1}");

  // mnożenie
  s = s.replace(/\*/g, "\\cdot ");
  s = s.replace(/·/g, "\\cdot ");

  // Top-level licznik/mianownik -> prawdziwy ułamek
  if (!s.includes("\\dfrac")) {
    const idx = findTopLevelSlash(s);
    if (idx !== -1) {
      const left = s.slice(0, idx).trim();
      const right = s.slice(idx + 1).trim();
      if (left && right) {
        s = `\\dfrac{${left}}{${right}}`;
      }
    }
  }

  return s;
}

// === GENERATE TASK ===
export function generateTask() {
  // a od 2 do 7
  const a = pick([2, 3, 4, 5, 6, 7]);

  // Losujemy liniową postać w wyniku: (b x + c)
  const b = pick([1, 2, 3]);
  const c = pick([-7, -6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6, 7]);

  // Żeby nie wyglądało zawsze tak samo, losujemy kolejność mnożenia.
  const orderSwap = Math.random() < 0.5;

  // (x+a)^2 jako trójmian
  const denomQuadTex = `x^2+${2 * a}x+${a * a}`; // (x+a)^2
  const linearFactorTex = `x+${a}`;

  // (b x + c)
  const linNumTex =
    b === 1 ? `x${c >= 0 ? "+" : ""}${c}` : `${b}x${c >= 0 ? "+" : ""}${c}`;

  // === UTRUDNIENIE: x*(bx+c) ma być ROZWINIĘTE w treści ===
  // x*(bx+c) = b x^2 + c x
  const expandedNumTex =
    b === 1
      ? `x^2${c >= 0 ? "+" : ""}${c === 1 ? "" : c === -1 ? "-" : c}x`
      : `${b}x^2${c >= 0 ? "+" : ""}${c}x`;

  // Przyda się do wyjaśnienia (pokazać wyłączenie x):
  const factoredNumTex = `x(${linNumTex})`;

  const firstFracNumTex = expandedNumTex; // <— tu jest „wymnożone”
  const firstFracDenTex = denomQuadTex;

  const secondFracNumTex = linearFactorTex;
  const secondFracDenTex = `x`;

  const exprTex = orderSwap
    ? `\\frac{${secondFracNumTex}}{${secondFracDenTex}}\\cdot\\frac{${firstFracNumTex}}{${firstFracDenTex}}`
    : `\\frac{${firstFracNumTex}}{${firstFracDenTex}}\\cdot\\frac{${secondFracNumTex}}{${secondFracDenTex}}`;

  const expectedNum = linNumTex;
  const expectedDen = linearFactorTex;

  return {
    type: "MultiplyAndSimplifyRationalTask",
    data: {
      a,
      b,
      c,
      orderSwap,
      exprTex,
      expectedNum,
      expectedDen,
      expandedNumTex,
      factoredNumTex,
      domainTex: `x\\neq 0\\ \\text{oraz}\\ x\\neq -${a}`,
    },
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>
        Uprość wyrażenie. Załóż, że <InlineMath math={data.domainTex} />.
      </p>
      <BlockMath math={data.exprTex} />
    </div>
  );
}

// === RENDER INPUT ===
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder={"np. (2x+3)/(x+5)"}
      showPreview
    />
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string") {
    return {
      isCorrect: false,
      expected: `(${data.expectedNum})/(${data.expectedDen})`,
    };
  }

  const [rawNum, rawDen] = splitFraction(userAnswer);
  if (!rawNum || !rawDen) {
    return {
      isCorrect: false,
      expected: `(${data.expectedNum})/(${data.expectedDen})`,
    };
  }

  const num = stripOuterParens(rawNum);
  const den = stripOuterParens(rawDen);

  const ok =
    normalizeSum(num) === normalizeSum(data.expectedNum) &&
    normalizeSum(den) === normalizeSum(data.expectedDen);

  return {
    isCorrect: ok,
    expected: `(${data.expectedNum})/(${data.expectedDen})`,
  };
}

// === RENDER ANSWER ===
export function renderAnswer({ data }) {
  return <InlineMath math={`\\frac{${data.expectedNum}}{${data.expectedDen}}`} />;
}

// === EXPLANATION ===
export function renderExplanation({ data }) {
  const a = data.a;
  const denomFact = `(x+${a})^2`;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Zauważ, że{" "}
        <InlineMath math={`x^2+${2 * a}x+${a * a}=${denomFact}`} />.
      </p>

      <p>
        <strong>Krok 2:</strong> W liczniku masz wielomian{" "}
        <InlineMath math={data.expandedNumTex} />. Wyłącz{" "}
        <InlineMath math={"x"} /> przed nawias:
      </p>
      <BlockMath math={`${data.expandedNumTex}=${data.factoredNumTex}`} />

      <p>
        <strong>Krok 3:</strong> Podstaw i skróć wspólne czynniki{" "}
        <InlineMath math={"x"} /> oraz <InlineMath math={`(x+${a})`} />:
      </p>
      <BlockMath
        math={`\\frac{${data.expandedNumTex}}{(x+${a})^2}\\cdot\\frac{(x+${a})}{x}
        =\\frac{x(${data.expectedNum})}{(x+${a})^2}\\cdot\\frac{(x+${a})}{x}
        =\\frac{${data.expectedNum}}{x+${a}}`}
      />

      <p className="text-sm opacity-90">
        Warunki <InlineMath math={data.domainTex} /> wynikają z tego, że w
        wyjściowym wyrażeniu występują mianowniki{" "}
        <InlineMath math={"x"} /> i <InlineMath math={`(x+${a})^2`} />.
      </p>
    </div>
  );
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(...args) {
  const ua = args.length === 1 ? args[0] : args[1];
  const raw = typeof ua === "string" ? ua : ua?.raw;
  if (!raw || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={toUserAnswerLatex(raw)} />;
}
